import { useMemo } from 'react';
import {
    type LoaderFunctionArgs,
    type SerializeFrom,
    json,
} from '@remix-run/node';
import {
    type UIMatch,
    useLoaderData,
    useMatches,
    useNavigate,
    useSearchParams,
} from '@remix-run/react';
import { ClientOnly } from 'remix-utils';
import dayjs from 'dayjs';
import invariant from 'tiny-invariant';

import type { APHandle } from '~/modules/root';
import {
    getCanonicalHreflang,
    createMetaTitle,
    mergeMeta,
    createMetaDescription,
} from '~/modules/seo';
import {
    getDigitalMagazine,
    type GetDigitalMagazineProps,
} from '@modules/magazine/api';
import { MagazineViewerModal } from '@modules/magazine/component/magazine-viewer/MagazineViewerModal';
import { BASE_URL } from '@modules/magazine/configs/constants';
import type { loader as parentLoader } from '~/routes/_app.digital-magazine._layout';

interface DigitalMagazineMetaProps {
    title: string;
    description: string;
}

const getDigitalMagazineMetaData = (
    data: GetDigitalMagazineProps | undefined,
): DigitalMagazineMetaProps | undefined => {
    if (data) {
        const { type, title, subtitle, month, year } = data;
        const formattedMonth = dayjs()
            .month(month - 1)
            .format('MMMM');

        switch (type) {
            case 'Build':
                return {
                    title: `Build ${subtitle} ${formattedMonth} ${year}`,
                    description: `The smarter way to build - explore materials, systems and performance. Explore Build ${subtitle} ${formattedMonth} ${year}.`,
                };
            case 'The Project Portfolio':
                return {
                    title: `The Project Portfolio ${subtitle} ${formattedMonth} ${year}`,
                    description: `The Project Portfolio celebrates exceptional architecture and design. Explore Project Portfolio ${subtitle} ${formattedMonth} ${year}.`,
                };
            case 'Curated Collection':
                return {
                    title: `Curated Collection ${subtitle} ${formattedMonth} ${year}`,
                    description: `Explore our curated collection ${subtitle} ${formattedMonth} ${year}. Discover a thoughtfully curated collection of the finest products & inspirations.`,
                };

            case 'ArchiPros Talk':
                return {
                    title: `ArchiPro's Talk ${subtitle} ${formattedMonth} ${year}`,
                    description: `Discover features from leading architects, designers and inspiring products. Explore ArchiPro's talk ${subtitle} ${formattedMonth} ${year} and find out more. `,
                };

            case 'Commercial Edit':
                return {
                    title: `The Commercial Edit ${subtitle} ${formattedMonth} ${year}`,
                    description: `Discover commercial products and innovations from the Commercial Edit ${subtitle} ${formattedMonth} ${year}. Find the best in business solutions today.`,
                };

            case 'Project Feature':
                if (title === 'Project of the Month') {
                    return {
                        title: `${formattedMonth} ${year} Project of the Month`,
                        description: `Discover ${formattedMonth}'s Project of the Month: Immerse in breathtaking architecture, find design inspiration, and ignite your creativity.`,
                    };
                }
                break;

            default:
                break;
        }
    }

    return undefined;
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const metaProps = getDigitalMagazineMetaData(data);

        const title = createMetaTitle(props, [
            metaProps?.title,
            data?.pageTitle,
        ]);

        const description = createMetaDescription([metaProps?.description]);

        const preloadImageLinks = data?.iframeLink
            ? [
                  {
                      rel: 'preload',
                      href: data.iframeLink,
                      as: 'document',
                      tagName: 'link',
                  },
              ]
            : [];

        return [
            { title },
            { description },
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];
    },
);

export const handle: APHandle = {
    isDialogRoute: true,
};

export type DigitalMagazineLoader = typeof loader;

export const loader = async ({ context, params }: LoaderFunctionArgs) => {
    invariant(params.urlSegment, 'A magazine URL segment is required');

    const [magazine] = await Promise.all([
        getDigitalMagazine({
            ...context,
            URLSegment: params.urlSegment,
        }),
    ]);

    return json({
        iframeLink: magazine.iframeLink,
        type: magazine.type,
        title: magazine.title,
        pageTitle: magazine.title,
        subtitle: magazine.subtitle ?? '',
        urlSegment: magazine.urlSegment,
        month: magazine.month,
        year: magazine.year,
    });
};

const DigitalMagazineViewerPage = () => {
    const { iframeLink, urlSegment } = useLoaderData<typeof loader>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const matches = useMatches();

    const toggleLink = useMemo(() => {
        const newParams = new URLSearchParams();
        for (const param of ['page', 'sort']) {
            const paramValue = searchParams.get(param);
            if (paramValue) {
                newParams.append(param, paramValue);
            }
        }
        const yearMonth = searchParams.get('year')
            ? `/${searchParams.get('year')}${searchParams.get('month') ? `/${searchParams.get('month')}` : ''}`
            : '';
        return `${BASE_URL}${yearMonth}?${newParams.toString()}`;
    }, [searchParams]);

    const parentRoute = matches.find((match) =>
        match.id.endsWith('digital-magazine._layout'),
    ) as UIMatch<SerializeFrom<typeof parentLoader>> | undefined;

    const paginationState = parentRoute?.data
        ? {
              list: parentRoute.data.magazineTiles.map(
                  (magazine) => magazine.urlSegment,
              ),
              page: parentRoute.data.pagination.page,
          }
        : { list: [], page: 1 };

    return (
        <>
            <ClientOnly>
                {() => (
                    <MagazineViewerModal
                        isOpen={true}
                        iframeLink={iframeLink}
                        urlSegment={urlSegment}
                        paginationState={paginationState}
                        toggleModal={() =>
                            navigate(toggleLink, { preventScrollReset: true })
                        }
                    />
                )}
            </ClientOnly>
        </>
    );
};

export default DigitalMagazineViewerPage;
